import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@plaidev/sour-react';
import { type ReactNode, type TouchEvent, useEffect, useRef, useState } from 'react';
import { PopoverMenuItem } from './popoverMenu.js';

export type LogoutMenuItemProps = {
  onLogout: () => void;
  /**
   * 親ポップオーバー（ユーザーメニュー）の開閉状態。HoverCard の content は body へ portal
   * されるため、親が閉じたあとも注釈だけ画面に残ることがある。AdminMenuSection と同じ理由で制御する。
   */
  parentOpen: boolean;
  /** 「詳細はこちら」のリンク先。未指定なら QANT サポートサイトの該当記事。 */
  helpHref?: string;
  /** false のときは host-only ログアウト。別環境に関する注釈を出さない。 */
  showCrossPlatformLogoutNote?: boolean;
};

const HELP_TITLE = '全環境からのログアウト';
const HELP_BODY =
  '現在の環境からログアウト時、紐づけされた別環境 (KARTE基盤/QANT基盤)がログアウトされていない可能性があるため、もう一方の環境を別タブで開き、ログアウト処理を行います。';
const DEFAULT_HELP_HREF = 'https://help.qant.jp/faqs/faq-0245/#5_2';

// touchstart からこれ以上動いたら、スクロールや取りやめとみなしログアウトしない。
const TOUCH_MOVE_CANCEL_THRESHOLD_PX = 10;

const LogoutMenuItemButton = ({
  onLogout,
  showCrossPlatformLogoutNote,
  onOpenHelp,
  onCloseHelpSoon,
  onOpenHelpFromTouch,
}: {
  onLogout: () => void;
  showCrossPlatformLogoutNote: boolean;
  onOpenHelp: () => void;
  onCloseHelpSoon: () => void;
  onOpenHelpFromTouch: (event: TouchEvent) => void;
}): ReactNode => {
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const loggedOutRef = useRef(false);

  const handleLogout = () => {
    if (loggedOutRef.current) return;
    loggedOutRef.current = true;
    onLogout();
  };

  return (
    <PopoverMenuItem
      onTouchStart={(event) => {
        const touch = event.touches[0];
        touchStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }}
      onTouchEnd={(event) => {
        const start = touchStartRef.current;
        touchStartRef.current = null;
        if (!start) return;
        event.preventDefault();
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        if (Math.hypot(dx, dy) > TOUCH_MOVE_CANCEL_THRESHOLD_PX) return;
        handleLogout();
      }}
      className={showCrossPlatformLogoutNote ? 'h-auto items-stretch gap-0 px-0' : undefined}
    >
      {showCrossPlatformLogoutNote ? (
        <>
          <div
            role="button"
            tabIndex={0}
            className="flex min-w-0 flex-1 flex-col outline-none pl-4 pr-3 py-2"
            onClick={handleLogout}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              handleLogout();
            }}
          >
            <span className="truncate">ログアウト</span>
            <span className="truncate text-xs font-normal text-muted">ログアウトし別環境を開く</span>
          </div>
          <span
            role="button"
            tabIndex={0}
            aria-label={HELP_TITLE}
            onPointerEnter={onOpenHelp}
            onPointerLeave={onCloseHelpSoon}
            onFocus={onOpenHelp}
            onBlur={onCloseHelpSoon}
            onClick={(event) => {
              event.stopPropagation();
              onOpenHelp();
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              onOpenHelp();
            }}
            onTouchEnd={onOpenHelpFromTouch}
            className="flex shrink-0 items-center cursor-pointer pr-4 text-muted outline-none hover:text-default"
          >
            <FontAwesomeIcon icon={faCircleQuestion} />
          </span>
        </>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className="flex min-w-0 flex-1 flex-col outline-none"
          onClick={handleLogout}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            handleLogout();
          }}
        >
          <span className="truncate">ログアウト</span>
        </div>
      )}
    </PopoverMenuItem>
  );
};

/**
 * ユーザーメニューのログアウト項目。cross-platform ログアウト時のみ、両基盤に及ぶことを
 * 補足文とヘルプアイコンの注釈（hover で開く HoverCard）で説明する。
 */
export function LogoutMenuItem({
  onLogout,
  parentOpen,
  helpHref = DEFAULT_HELP_HREF,
  showCrossPlatformLogoutNote = false,
}: LogoutMenuItemProps): ReactNode {
  const [helpOpen, setHelpOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const clearCloseTimer = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = undefined;
  };
  const openHelp = () => {
    clearCloseTimer();
    setHelpOpen(true);
  };
  const closeHelpSoon = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setHelpOpen(false), 150);
  };

  useEffect(() => {
    if (!parentOpen) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = undefined;
      setHelpOpen(false);
    }
  }, [parentOpen]);
  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const menuItem = (
    <LogoutMenuItemButton
      onLogout={onLogout}
      showCrossPlatformLogoutNote={showCrossPlatformLogoutNote}
      onOpenHelp={openHelp}
      onCloseHelpSoon={closeHelpSoon}
      onOpenHelpFromTouch={(event) => {
        event.stopPropagation();
        openHelp();
      }}
    />
  );

  if (!showCrossPlatformLogoutNote) return menuItem;

  return (
    <HoverCard open={parentOpen && helpOpen} onOpenChange={() => undefined}>
      <HoverCardTrigger asChild>{menuItem}</HoverCardTrigger>
      <HoverCardContent
        side="right"
        align="end"
        className="w-[288px] p-4"
        onPointerEnter={openHelp}
        onPointerLeave={closeHelpSoon}
        onFocus={openHelp}
        onBlur={closeHelpSoon}
      >
        <div className="flex flex-col gap-2 text-sm text-default">
          <span className="font-bold">{HELP_TITLE}</span>
          <p className="font-normal leading-relaxed">{HELP_BODY}</p>
          <a href={helpHref} target="_blank" rel="noopener noreferrer" className="font-normal text-primary underline">
            詳細はこちら
          </a>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
